import * as Dialog from '@radix-ui/react-dialog'
import type { KeyboardEvent, ReactNode } from 'react'
import { Button } from '../primitives/Button'
import s from './Drawer.module.css'

/**
 * The detail of one thing (a product, a retailer, an order), opened from a list. On a laptop it slides in from the
 * right and the list stays in view behind it, so clicking another row swaps the detail. On a phone it fills the
 * screen with a back button.
 *
 * Keep what's open in the URL with `useParam`, so a link opens the same detail and Back closes it:
 *
 *   const [productId, openProduct] = useParam('product')
 *   <Drawer open={productId !== null} onClose={() => openProduct(null)} label="Product" title={…}>…</Drawer>
 *
 * `onPrev` / `onNext` add up and down buttons and the j / k keys, so you can walk the list without leaving it.
 */
export function Drawer(props: {
  open: boolean
  onClose: () => void
  /** What kind of thing this is ("Product"). */
  label: string
  /** Its name, once known. */
  title?: string
  /** Where Back goes on a phone: the screen's title ("Today"). */
  backLabel?: string
  onPrev?: () => void
  onNext?: () => void
  actions?: ReactNode
  children: ReactNode
}) {
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement
    if (target.closest('input, textarea, select, [contenteditable="true"]')) return
    if ((e.key === 'j' || e.key === 'ArrowDown') && props.onNext) {
      e.preventDefault()
      props.onNext()
    } else if ((e.key === 'k' || e.key === 'ArrowUp') && props.onPrev) {
      e.preventDefault()
      props.onPrev()
    }
  }
  return (
    <Dialog.Root open={props.open} onOpenChange={(open) => !open && props.onClose()} modal={false}>
      <Dialog.Portal>
        <Dialog.Content
          className={s.drawer}
          onKeyDown={onKey}
          // Clicking the list behind keeps the drawer open, so another row can replace what it shows.
          onInteractOutside={(e) => e.preventDefault()}
          // Focus lands on the drawer itself, so j / k and Esc work at once and no button looks pressed.
          onOpenAutoFocus={(e) => {
            e.preventDefault()
            ;(e.currentTarget as HTMLElement | null)?.focus()
          }}
          aria-describedby={undefined}
        >
          <div className={s.bar}>
            <span className={s.back}>
              <Button variant="ghost" icon="back" onClick={props.onClose}>
                {props.backLabel ?? 'Back'}
              </Button>
            </span>
            <span className={s.label}>{props.label}</span>
            <span className={s.tools}>
              {props.onPrev && <Button variant="ghost" icon="up" iconOnly label="Previous (k)" onClick={props.onPrev} />}
              {props.onNext && <Button variant="ghost" icon="down" iconOnly label="Next (j)" onClick={props.onNext} />}
              <span className={s.close}>
                <Button variant="ghost" icon="close" iconOnly label="Close (Esc)" onClick={props.onClose} />
              </span>
            </span>
          </div>
          <div className={s.body}>
            <Dialog.Title className={s.title}>{props.title ?? props.label}</Dialog.Title>
            {props.children}
          </div>
          {props.actions && <div className={s.actions}>{props.actions}</div>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
